import { Component, Input, OnChanges, OnInit, OnDestroy, Output, EventEmitter, ElementRef, ViewChild } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { animate, sequence, state, style, transition, trigger } from '@angular/animations';
import { filter, map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { AwardsService, AwardProgramInfo } from 'services/awards/awards.service';
import { UserInfo } from 'services/user/user.service';

@Component({
    selector: 'app-nav-menu',
    styleUrls: ['navmenu.component.css'],
    templateUrl: 'navmenu.component.html',
    animations: [
        trigger('iconspinner', [
            state(
                'false',
                style({
                    transform: 'rotate(0deg)',
                })
            ),
            state(
                'true',
                style({
                    transform: 'rotate(90deg)',
                })
            ),
            transition('false <=> true', animate('100ms')),
        ]),
        trigger('submenuexpand', [
            transition(':enter', [
                style({
                    height: '0',
                    opacity: '0',
                }),
                sequence([
                    animate(
                        '100ms ease-in',
                        style({
                            height: '*',
                        })
                    ),
                    animate(
                        '200ms ease-in',
                        style({
                            opacity: '1',
                        })
                    ),
                ]),
            ]),
            transition(':leave', [
                style({
                    height: '*',
                    opacity: '1',
                }),
                sequence([
                    animate(
                        '200ms ease-out',
                        style({
                            opacity: '0',
                        })
                    ),
                    animate(
                        '100ms ease-out',
                        style({
                            height: '0',
                        })
                    ),
                ]),
            ]),
        ]),
    ],
})

export class NavMenuComponent implements OnChanges, OnInit, OnDestroy {
    @ViewChild('navmenu') navmenu: ElementRef;

    private _subRelease: Subject<void> = new Subject<void>();

    @Input() items: NavMenuItem[];
    @Input() logout: Function;
    @Input() about: Function;
    @Input() user: UserInfo;

    @Output() navigate: EventEmitter<string> = new EventEmitter<string>();

    CurrentAward: AwardProgramInfo;
    FilteredItems: NavMenuItem[];

    constructor(private awardsService: AwardsService, private router: Router) {}

    // #region Clear All Active Buttons Method

    ClearAllActiveButtons() {
        // Clear all active flags
        if (this.FilteredItems && this.FilteredItems.length > 0) {
            this.FilteredItems.forEach(item => {
                item.Active = false;
            });
        }
    }

    // #endregion

    HandleItemClick(item: NavMenuItem) {
        const self = this;

        if (item == null) {
            return;
        }

        if (item.IsLogout === true || item.IsAbout === true) {
            // Get a handle to the currently active nav item.
            let curActive: NavMenuItem = null;

            for (let item of self.FilteredItems) {
                if (item.Active) {
                    curActive = item;
                    break;
                }

                if (item.Submenus && !!item.Submenus.length) {
                    for (let sub of item.Submenus) {
                        if (sub.Active) {
                            curActive = sub;
                            break;
                        }
                    }
                    if (curActive) {
                        break;
                    }
                }
            }

            // Clear all actives
            this.ClearAllActiveButtons();

            // Set the About button as active visually.
            item.Active = true;

            // Call the appropriate method, and wait for the dialog to dismiss.
            const methodFn = item.IsLogout ? self.logout : self.about;

            methodFn()
                .pipe(takeUntil(self._subRelease))
                .subscribe(() => {
                    // Clear the about / logout button as active.
                    item.Active = false;

                    // Restore focus to the previously active item (if any)
                    if (curActive) {
                        curActive.Active = true;

                        const btn = self.navmenu.nativeElement.querySelector('#NavBtn_' + curActive.ButtonIndex);
                        if (btn) {
                            btn.focus();
                        }
                    }
                });
            return;
        }

        // Is this a parent element for a set of submenus? If so, this click action only expands/collapses.
        if (item.Submenus && !!item.Submenus.length) {
            item.IsExpanded = !item.IsExpanded;
            return;
        }

        self.navigate.emit((item.ActionRoute || [''])[0]);

        // Attempt to navigate to the action route, if one is defined.
        if (item.ActionRoute && !!item.ActionRoute.length) {
            // Parse out the ~ (active current route) so we can maintain integrity of the current award system.
            const awardsUrl = self.CurrentAward.URLPath;
            const newroute = item.ActionRoute.map(ar => {
                return typeof ar === 'string' ? ar.replace('~', awardsUrl) : ar;
            });

            self.router.navigate(newroute);
        }
    }

    ngOnDestroy() {
        this._subRelease.next();
        this._subRelease.complete();
    }

    ngOnInit() {
        const self = this;

        self.CurrentAward = null;

        // Hook into the router event structure so we can monitor successful route changes in order to
        // update the active button (and also remove buttons which may not apply).
        self.router.events
            .pipe(
                takeUntil(self._subRelease),
                filter(event => event instanceof NavigationEnd),
                map(event => {
                    const navev: NavigationEnd = <NavigationEnd>event;
                    return navev.url;
                })
            )
            .subscribe(url => {
                self.UpdateMenuItems(url);
            });

        // Subscribe to changes on the current award.
        self.awardsService.CurrentAwardsSub.pipe(takeUntil(self._subRelease)).subscribe(award => {
            self.CurrentAward = award;
        });
    }

    ngOnChanges() {
        const self = this;
        const curRole = self.user && self.user.CurrentRole;

        // Process the menu items against the user (if available) to determine which are available.
        const fitems = (self.items || []).filter(itm => {
            if (!itm.Exclude) {
                if (itm.AllowedRoles.includes('all')) {
                    return itm;
                }
                if (itm.AllowedRoles.includes(curRole)) {
                    if (curRole == 'Designee') {
                        if (itm.DesigneeRights != null) {
                            let designeeRole = self.user.Roles.find(r => r.RoleName === 'Designee' && r.WorkLocation === self.user.SchoolNumber);
                            for (let r of designeeRole.Rights) {
                                if (itm.DesigneeRights.includes(r)) {
                                    return itm;
                                }
                            }
                        }
                    }
                    else {
                        return itm;
                    }
                }
            }
        });

        self.FilteredItems = fitems;

        if (self.FilteredItems && self.FilteredItems.length) {
            for (let i = 0, j = self.FilteredItems.length; i < j; i++) {
                self.FilteredItems[i].ButtonIndex = i;
            }
        }

        // Initial update the url we are currently at.
        self.UpdateMenuItems(self.router.routerState.snapshot.url);
    }

    UpdateMenuItems(url: string) {
        // Clear all active flags
        if (this.FilteredItems && this.FilteredItems.length > 0) {
            this.FilteredItems.forEach(item => {
                item.Active = false;
                if (item.Submenus && !!item.Submenus.length) {
                    item.Submenus.forEach(sitem => (sitem.Active = false));
                }
            });
        }

        // Check the url against all of our menu items to look for a match.
        if (this.FilteredItems && this.FilteredItems.length > 0) {
            const parsedUrl = url.replace(/^\/(.*?)\/(.*)$/, '~/$2');

            this.FilteredItems.forEach(item => {
                if (
                    item.LinkedStates &&
                    item.LinkedStates.find(value => {
                        // return (url.indexOf(value) >= 0 || url.replace(/^\/(.*?)\/([^\/]*)(\/.*)?$/, '~/$2').indexOf(value) >= 0);
                        return value.indexOf(url) >= 0 || value.indexOf(parsedUrl) >= 0;
                    }) != null
                ) {
                    // Found a match on this item. Set the button to being active.
                    item.Active = true;
                } else {
                    // No match found on this item. Does this item have submenus? If so, check them.
                    if (item.Submenus && !!item.Submenus.length) {
                        item.Submenus.forEach(subitem => {
                            if (
                                subitem.LinkedStates &&
                                subitem.LinkedStates.find(svalue => {
                                    return svalue.indexOf(url) >= 0 || svalue.indexOf(parsedUrl) >= 0;
                                }) != null
                            ) {
                                // Found a match on a subitem. Set the button to being active.
                                subitem.Active = true;

                                // Also set the containing item to expanded.
                                item.IsExpanded = true;
                            }
                        });
                    }
                }
            });
        }
    }
}

export class NavMenuItem {
    ButtonIndex?: number;
    MenuText: string;
    MenuIcon?: string;
    ActionRoute?: any[];
    LinkedStates?: string[];
    AllowedRoles?: string[];
    DesigneeRights?: string[];
    IsAbout?: boolean;
    IsHome?: boolean;
    IsLogout?: boolean;
    Color?: string;
    Active?: boolean;
    Exclude?: boolean;
    Disabled?: boolean;
    IsExpanded?: boolean;
    Submenus?: NavMenuItem[];
}
